import { useEffect, useRef, useState } from "react";

// Shared countdown engine used by every timer in the app (OTP rings, resend
// timers, SMS-link resend). Time is derived from a start timestamp
// (Date.now()), never from decrementing a counter, so delayed ticks (busy main
// thread, background tabs) cannot make it drift or skip. Ticks are scheduled
// for the exact moment the displayed second changes.

export interface CountdownTick {
  /** Whole seconds still to show (ceil of the remaining time). */
  secondsLeft: number;
  /** Milliseconds until secondsLeft next changes (0 when finished). */
  msUntilNextTick: number;
}

export interface StartCountdownOptions {
  durationSeconds: number;
  onTick?: (tick: CountdownTick) => void;
  onComplete?: () => void;
  /** Injectable clock for tests. */
  now?: () => number;
}

/** Starts a countdown; returns a cancel function. Safe to call from callbacks. */
export function startCountdown({
  durationSeconds,
  onTick,
  onComplete,
  now = Date.now,
}: StartCountdownOptions): () => void {
  const totalMs = Math.max(0, durationSeconds) * 1000;
  const endAt = now() + totalMs;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let cancelled = false;

  const tick = () => {
    if (cancelled) return;
    const remaining = Math.max(0, endAt - now());
    if (remaining <= 0) {
      onTick?.({ secondsLeft: 0, msUntilNextTick: 0 });
      onComplete?.();
      return;
    }
    const secondsLeft = Math.ceil(remaining / 1000);
    const msUntilNextTick = remaining - (secondsLeft - 1) * 1000;
    onTick?.({ secondsLeft, msUntilNextTick });
    timeoutId = setTimeout(tick, msUntilNextTick);
  };

  tick();
  return () => {
    cancelled = true;
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  };
}

export interface UseCountdownOptions {
  /** Change to restart the countdown (e.g. on resend). */
  resetKey?: unknown;
  /** false pauses/hides; the countdown (re)starts when it becomes true. */
  running?: boolean;
  onComplete?: () => void;
}

export interface CountdownState {
  secondsLeft: number;
  /**
   * Ring fill (1 = full, 0 = empty) to animate TOWARDS, with a linear
   * transition of `transitionMs`. It always targets the value for the moment
   * the number next changes, so ring and number land together and the ring is
   * empty exactly when the number reaches 0.
   */
  progress: number;
  transitionMs: number;
  done: boolean;
}

export function useCountdown(
  durationSeconds: number,
  { resetKey, running = true, onComplete }: UseCountdownOptions = {}
): CountdownState {
  const totalMs = Math.max(0, durationSeconds) * 1000;
  const full: CountdownState = { secondsLeft: durationSeconds, progress: 1, transitionMs: 0, done: false };
  const [state, setState] = useState<CountdownState>(full);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!running || totalMs <= 0) return;
    // Paint the full ring first (no transition), then start draining.
    setState(full);
    let cancel: (() => void) | undefined;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        cancel = startCountdown({
          durationSeconds,
          onTick: ({ secondsLeft, msUntilNextTick }) =>
            setState({
              secondsLeft,
              progress: Math.max(0, ((secondsLeft - 1) * 1000) / totalMs),
              transitionMs: msUntilNextTick,
              done: secondsLeft === 0,
            }),
          onComplete: () => onCompleteRef.current?.(),
        });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      cancel?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationSeconds, resetKey, running]);

  return state;
}
